/**
 * ArkTableInline - 行内「点击单元格直接编辑」键盘 + 焦点桥
 *
 * 仅在「活动单元格」(data-ark-active-cell) 内拦截 Tab / Enter / Escape，派发 .NET
 * 决定是否 preventDefault；并提供 focusActiveCell 聚焦活动格内首个可聚焦控件。
 *
 * 失焦自动保存：监听 focusout，当焦点离开整个编辑区（点表外 / 走开）时派发 OnInlineBlur
 * 让 .NET 自动保存当前行。用 suppressBlur 标志过滤键盘导航(Tab/Enter/Esc)引发的重渲 focusout，
 * 并跳过焦点仍落在本表内的情形（交给 click/键盘逻辑），避免编辑被中途误提交。
 *
 * 指针级点击外退出（document pointerdown 兜底）：无焦点编辑器（ArkSelect 触发器是 div，
 * 单元格内从未有元素持焦）不产生 focusout，失焦通道对其整体失效 → 由 document 级 pointerdown
 * 判定「点在编辑面之外」直接派发 OnInlineBlur。仅在活动格内无焦点时接管：有焦点时必须让位给
 * focusout 通道，它保证 change（输入值回传）先于提交的事件顺序，抢跑会提交旧值。
 *
 * 照 ark-shortcuts.js 范式：capture 阶段 keydown + invokeMethodAsync 返 bool 驱动
 * preventDefault + register/unregister 对称 + window 全局 + export default。进 index.js 静态打包。
 *
 * 用法：
 *   ArkTableInline.register(tableId, dotnetRef)
 *     dotnetRef 须暴露 [JSInvokable] Task<bool> OnInlineKeyDown(string key, bool shift)
 *                     与 [JSInvokable] Task OnInlineBlur()
 *   ArkTableInline.focusActiveCell(tableId)
 *   ArkTableInline.unregister(tableId)
 */

const instances = new Map();

const ArkTableInline = {
    register(tableId, dotnetRef) {
        if (instances.has(tableId)) this.unregister(tableId);

        const root = document.querySelector(`[data-ark-table="${tableId}"]`);
        if (!root) return;

        const inst = { root, suppressBlur: false, suppressTimer: 0, pointerOnEditCell: false, pointerTimer: 0 };

        inst.onKey = (e) => {
            // 仅当焦点在「活动编辑格」内才接管，避免吞掉搜索框等其它输入
            const cell = e.target.closest('[data-ark-active-cell]');
            if (!cell) return;
            // IME（中日文拼音）合成中放行，避免拼音中途 Enter/Tab 误触
            if (e.isComposing || e.keyCode === 229) return;
            if (e.key !== 'Tab' && e.key !== 'Enter' && e.key !== 'Escape') return;

            // 同步阻止默认：这三键在活动格内恒由 .NET 接管，必须立刻挡掉浏览器默认 Tab 移焦 / Enter 提交。
            // 若放到下面 invokeMethodAsync().then() 里（异步），浏览器会先把焦点移走，随后 focusActiveCell
            // 再异步抢回 → 焦点打架（新增行 Tab 到下一格聚焦失败）。
            e.preventDefault();
            e.stopPropagation();

            // 导航键会引发活动格重渲/切格 focusout —— 抑制紧随的失焦自动保存（否则编辑会被中途提交）
            inst.suppressBlur = true;
            clearTimeout(inst.suppressTimer);
            inst.suppressTimer = setTimeout(() => { inst.suppressBlur = false; }, 250);

            // 单行 <input> 不在 <form> 里时，按 Enter/Tab 不会触发 change → 当前输入值还没回传 Blazor。
            // 提交/切格前先同步派发一次 change 把值刷给服务端（Blazor 事件按到达顺序串行处理，
            // 此 change 会先于下面的 OnInlineKeyDown 被处理），否则保存的是旧值、甚至被「未改动」脏检查跳过。
            // 仅对文本类输入派发：checkbox/radio(开关/勾选)点击即 change，再派发会误翻转其值；Escape 丢弃不刷。
            const t = e.target;
            const type = (t.type || '').toLowerCase();
            const isTextLike = t.tagName === 'TEXTAREA'
                || (t.tagName === 'INPUT' && type !== 'checkbox' && type !== 'radio');
            if (e.key !== 'Escape' && isTextLike) {
                t.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // 默认行为已同步阻止，这里只派发；返回值无需再驱动 preventDefault（异步太晚）。
            dotnetRef.invokeMethodAsync('OnInlineKeyDown', e.key, e.shiftKey)
                .catch(err => console.warn('[ArkTableInline] keydown dispatch failed:', err));
        };

        inst.onBlur = (e) => {
            // 仅关心活动编辑格失焦
            const cell = e.target.closest('[data-ark-active-cell]');
            if (!cell) return;
            // 键盘导航引发的重渲 focusout：跳过（由 OnInlineKeyDown 处理切格/提交）
            if (inst.suppressBlur) return;
            // 失焦由「点击另一个可编辑单元格」引起（会被 click→ActivateCell 接管切格）：不自动保存。
            // 关键：Blazor Server 下新活动格靠服务端往返后才 focus，远晚于本次 focusout，单凭下面的
            // activeElement 判定会误判「焦点已离表」→ 对半填的新增行误提交（点第二格即保存的真因）。
            // 仅限「可编辑格」：点只读格/工具栏/表外仍照常走下面的判定提交退出（否则编辑无法点击退出）。
            if (inst.pointerOnEditCell) return;
            // 焦点直接落在本表内另一个可聚焦元素：交给 click/键盘逻辑，不触发失焦保存
            if (e.relatedTarget && inst.root.contains(e.relatedTarget)) return;
            // 延一拍再判最终焦点落点，避免瞬时 focusout 误判；确认焦点确已离开本表才自动保存
            setTimeout(() => {
                if (!instances.has(tableId)) return;            // 已 unregister
                const active = document.activeElement;
                if (active && active !== document.body && inst.root.contains(active)) return;
                dotnetRef.invokeMethodAsync('OnInlineBlur')
                    .catch(err => console.warn('[ArkTableInline] blur dispatch failed:', err));
            }, 0);
        };

        // 记录「指针正按在某个可编辑单元格上」：仅这类点击会被 click→ActivateCell 接管切格，
        // 失焦时应交给它、不自动保存。点只读格/工具栏/表外都不记，失焦照常提交退出（修复「点别处不退出」）。
        // pointerdown 早于 blur/focusout 触发，故 onBlur 能据此判定。
        inst.onPointerDown = (e) => {
            if (e.target.closest('[data-ark-edit-cell]')) {
                inst.pointerOnEditCell = true;
                clearTimeout(inst.pointerTimer);
                inst.pointerTimer = setTimeout(() => { inst.pointerOnEditCell = false; }, 400);
            }
        };

        // 指针级「点击外」退出兜底（无焦点编辑器专用，如 ArkSelect div 触发器）：
        // - 活动格内有焦点元素 → 让位 focusout 通道（其顺序保证 change 先于提交，这里抢跑会存旧值）
        // - 点在本表活动格内（含 ArkSelect 下拉面板：DOM 在活动格内、仅 position:fixed 绘制）→ 继续编辑
        // - 点在本表可编辑格上 → 交给 click→ActivateCell 切格（内部先提交当前行）
        // - 其余（只读格/工具栏/表外）→ 派发 OnInlineBlur 提交退出；服务端有 _draft==null 守卫，
        //   与 focusout 通道偶发双触发是幂等安全的
        inst.onDocPointerDown = (e) => {
            if (!inst.root.querySelector('[data-ark-active-cell]')) return;   // 没在编辑
            const focused = document.activeElement;
            if (focused && focused.closest && focused.closest('[data-ark-active-cell]')
                && inst.root.contains(focused)) return;                       // 有焦点：focusout 通道负责
            const t = e.target instanceof Element ? e.target : null;
            if (t) {
                const activeCell = t.closest('[data-ark-active-cell]');
                if (activeCell && inst.root.contains(activeCell)) return;
                const editCell = t.closest('[data-ark-edit-cell]');
                if (editCell && inst.root.contains(editCell)) return;
            }
            dotnetRef.invokeMethodAsync('OnInlineBlur')
                .catch(err => console.warn('[ArkTableInline] outside-pointer dispatch failed:', err));
        };

        root.addEventListener('keydown', inst.onKey, { capture: true });
        root.addEventListener('focusout', inst.onBlur);
        root.addEventListener('pointerdown', inst.onPointerDown, { capture: true });
        document.addEventListener('pointerdown', inst.onDocPointerDown, { capture: true });
        instances.set(tableId, inst);
    },

    focusActiveCell(tableId) {
        const inst = instances.get(tableId);
        if (!inst) return;
        // [tabindex] 兜底：ArkSelect 等无原生 input 的编辑器，Compact 时触发器带 tabindex=0，
        // 聚焦它才能让 focusout 失焦保存 / Enter / Esc / Tab 键盘桥对下拉格生效
        const el = inst.root.querySelector(
            '[data-ark-active-cell] input, [data-ark-active-cell] select, [data-ark-active-cell] textarea, [data-ark-active-cell] [tabindex]');
        if (el) {
            el.focus();
            if (typeof el.select === 'function') {
                try { el.select(); } catch (_) { /* number/email 等不支持 select 的输入忽略 */ }
            }
        }
    },

    unregister(tableId) {
        const inst = instances.get(tableId);
        if (!inst) return;
        inst.root.removeEventListener('keydown', inst.onKey, { capture: true });
        inst.root.removeEventListener('focusout', inst.onBlur);
        inst.root.removeEventListener('pointerdown', inst.onPointerDown, { capture: true });
        document.removeEventListener('pointerdown', inst.onDocPointerDown, { capture: true });
        clearTimeout(inst.suppressTimer);
        clearTimeout(inst.pointerTimer);
        instances.delete(tableId);
    }
};

window.ArkTableInline = ArkTableInline;

export default ArkTableInline;
